import { Component, OnInit } from '@angular/core';
import { UserService } from 'src/app/services/user/user.service';
import { mobiscroll, MbscDatetimeOptions } from '@mobiscroll/angular';
import * as alertifyjs from 'alertifyjs';

mobiscroll.settings = {
  lang: 'es',
  theme: 'ios',
  themeVariant: 'light'
};

@Component({
  selector: 'app-quota-history-global',
  templateUrl: './quota-history-global.component.html',
  styleUrls: ['./quota-history-global.component.css']
})
export class QuotaHistoryGlobalComponent implements OnInit {

  // ============================================
  // PROPIEDADES
  // ============================================
  
  executives: any[] = [];
  allExecutives: any[] = []; // Lista completa para filtrado local
  selectedExecutive: string = '';
  selectedExecutiveName: string = '';
  quotaHistory: any[] = [];
  
  loadingExecutives = false;
  loadingHistory = false;
  
  historyPage = 1;
  historyLimit = 10;
  historyTotal = 0;
  historyTotalPages = 0;
  
  // Filtros de fecha - Mobiscroll usa Date objects para rangos
  dateRangeStart: Date | null = null;
  dateRangeEnd: Date | null = null;
  
  // Configuración de Mobiscroll Date Picker (Desktop)
  desktopDateSettings: MbscDatetimeOptions = {
    display: 'bubble',
    touchUi: false,
    lang: 'es',
    theme: 'ios',
    themeVariant: 'light'
  };

  // Configuración de Mobiscroll Date Picker (Mobile)
  mobileDateSettings: MbscDatetimeOptions = {
    display: 'bottom',
    lang: 'es',
    theme: 'ios',
    themeVariant: 'light'
  };
  
  // Control de búsqueda
  canSearch = false;
  
  // Búsqueda en selector
  executiveSearchTerm: string = '';
  showExecutiveDropdown: boolean = false;

  constructor(private _user: UserService) {}

  ngOnInit(): void {
    this.loadExecutives();
  }

  // ============================================
  // CARGA DE EJECUTIVOS
  // ============================================
  
  loadExecutives = async () => {
    try {
      this.loadingExecutives = true;
      const response = await this._user.getExecutivesList();
      
      if (response?.success) {
        this.allExecutives = response.data;
        this.executives = [...this.allExecutives]; // Copia para filtrado
      }
    } catch (error) {
      console.error('Error al cargar ejecutivos:', error);
      alertifyjs.error('Error al cargar la lista de ejecutivos');
    } finally {
      this.loadingExecutives = false;
    }
  };

  // ============================================
  // FILTRADO LOCAL DE EJECUTIVOS
  // ============================================
  
  filterExecutives = () => {
    if (!this.executiveSearchTerm || this.executiveSearchTerm.trim() === '') {
      this.executives = [...this.allExecutives];
    } else {
      const searchLower = this.executiveSearchTerm.toLowerCase().trim();
      this.executives = this.allExecutives.filter(exec => 
        exec.nombreCompleto.toLowerCase().includes(searchLower)
      );
    }
  };

  toggleExecutiveDropdown = () => {
    this.showExecutiveDropdown = !this.showExecutiveDropdown;
  };

  selectExecutive = (executive: any) => {
    this.selectedExecutive = executive.code;
    this.selectedExecutiveName = executive.nombreCompleto;
    this.showExecutiveDropdown = false;
    this.executiveSearchTerm = '';
    this.executives = [...this.allExecutives]; // Resetear filtro
    this.onExecutiveChange();
    
    // Ejecutar búsqueda automáticamente
    this.searchHistory();
  };

  clearExecutiveSelection = () => {
    this.selectedExecutive = '';
    this.selectedExecutiveName = '';
    this.executiveSearchTerm = '';
    this.executives = [...this.allExecutives];
    this.onExecutiveChange();
    
    // Limpiar resultados al limpiar selección
    this.quotaHistory = [];
    this.historyTotal = 0;
    this.historyTotalPages = 0;
  };

  // ============================================
  // MANEJO DE SELECCIÓN DE EJECUTIVO
  // ============================================
  
  onExecutiveChange = () => {
    this.canSearch = !!this.selectedExecutive;
    // Limpiar resultados anteriores
    this.quotaHistory = [];
    this.historyTotal = 0;
    this.historyTotalPages = 0;
    this.historyPage = 1;
  };

  // ============================================
  // BÚSQUEDA DE HISTORIAL
  // ============================================
  
  searchHistory = async () => {
    if (!this.selectedExecutive) {
      alertifyjs.warning('Debe seleccionar un ejecutivo');
      return;
    }
    
    try {
      this.loadingHistory = true;
      
      const params: any = {
        codeExecutive: this.selectedExecutive,
        page: this.historyPage,
        limit: this.historyLimit,
        isGlobal: true  // Consulta global - incluir información de persona afectada
      };

      // Agregar filtros de fecha si están completos
      if (this.dateRangeStart && this.dateRangeEnd) {
        params.fechaInicio = this.formatDateToMMDDYYYY(this.dateRangeStart);
        params.fechaFin = this.formatDateToMMDDYYYY(this.dateRangeEnd);
      }

      const response = await this._user.getQuotaHistory(params);
      
      if (response?.success) {
        this.quotaHistory = response.data;
        this.historyTotal = response.pagination.totalRecords;
        this.historyTotalPages = response.pagination.totalPages;
        
        if (this.quotaHistory.length === 0) {
          alertifyjs.notify('No se encontraron resultados para los filtros seleccionados', 'warning', 3);
        } else {
          alertifyjs.success(`Se encontraron ${this.historyTotal} registro(s)`);
        }
      }
    } catch (error) {
      console.error('Error al cargar historial de cupos:', error);
      alertifyjs.error('Error al cargar el historial de cupos');
    } finally {
      this.loadingHistory = false;
    }
  };

  // ============================================
  // UTILIDADES DE CONVERSIÓN DE FECHAS
  // ============================================
  
  formatDateToMMDDYYYY(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  // ============================================
  // PAGINACIÓN
  // ============================================
  
  changeHistoryPage = (page: number) => {
    if (page >= 1 && page <= this.historyTotalPages) {
      this.historyPage = page;
      this.searchHistory();
    }
  };

  // ============================================
  // LIMPIAR FILTROS
  // ============================================
  
  clearFilters = () => {
    this.dateRangeStart = null;
    this.dateRangeEnd = null;
    this.historyPage = 1;
    
    if (this.selectedExecutive) {
      this.searchHistory();
    }
  };

  // ============================================
  // VISUALIZACIÓN - HELPERS
  // ============================================
  
  getQuotaChangeClass = (quotaAntigua: number, quotaNueva: number): string => {
    if (quotaNueva > quotaAntigua) return 'text-success';
    if (quotaNueva < quotaAntigua) return 'text-danger';
    return 'text-secondary';
  };

  getQuotaChangeIcon = (quotaAntigua: number, quotaNueva: number): string => {
    if (quotaNueva > quotaAntigua) return 'fas fa-arrow-up';
    if (quotaNueva < quotaAntigua) return 'fas fa-arrow-down';
    return 'fas fa-minus';
  };

  getTruncatedCode = (code: string): string => {
    if (!code) return '';
    // Truncar a los primeros 8 caracteres
    return code.length > 8 ? code.substring(0, 8) + '...' : code;
  };

  /**
   * Copiar código de cliente al portapapeles
   */
  copyClientCode = (code: string) => {
    if (!code) {
      alertifyjs.warning('No hay código para copiar');
      return;
    }

    // Usar la API del portapapeles
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code)
        .then(() => {
          alertifyjs.success('Código copiado al portapapeles');
        })
        .catch(err => {
          console.error('Error al copiar:', err);
          this.fallbackCopyToClipboard(code);
        });
    } else {
      // Fallback para navegadores antiguos
      this.fallbackCopyToClipboard(code);
    }
  };

  /**
   * Método fallback para copiar en navegadores antiguos
   */
  private fallbackCopyToClipboard = (text: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        alertifyjs.success('Código copiado al portapapeles');
      } else {
        alertifyjs.error('No se pudo copiar el código');
      }
    } catch (err) {
      console.error('Error al copiar:', err);
      alertifyjs.error('Error al copiar el código');
    }

    document.body.removeChild(textArea);
  };
}

